// Master Data API Service
// Handles all configurable master data from SuperAdmin

import { API_BASE_URL } from './apiConfig';

const getStoredToken = (): string => {
  const directToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (directToken && directToken.trim()) return directToken.trim();

  try {
    const currentUserRaw = localStorage.getItem('currentUser');
    if (!currentUserRaw) return '';
    const currentUser = JSON.parse(currentUserRaw);
    return typeof currentUser?.token === 'string' ? currentUser.token.trim() : '';
  } catch {
    return '';
  }
};

const buildHeaders = (): HeadersInit => {
  const token = getStoredToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  };
};

const getApiBaseUrl = (): string => API_BASE_URL;

// Master Data Types
export type MasterDataType = 
  | 'cuisine-types'
  | 'meal-types'
  | 'food-categories'
  | 'amenities'
  | 'dietary-tags'
  | 'dietary-preferences'
  | 'food-tags'
  | 'spice-levels'
  | 'serving-sizes'
  | 'payment-methods'
  | 'payment-statuses'
  | 'order-statuses'
  | 'delivery-statuses'
  | 'delivery-zones'
  | 'table-statuses'
  | 'reservation-statuses'
  | 'offer-categories'
  | 'booking-statuses'
  | 'review-ratings'
  | 'complaint-categories'
  | 'timezones'
  | 'currencies'
  | 'languages'
  | 'price-ranges'
  | 'seating-preferences'
  | 'discount-types'
  | 'order-types'
  | 'occasion-types'
  | 'allergen-types'
  | 'unit-of-measurements'
  | 'time-slots'
  | 'tax-configurations'
  | 'user-roles'
  | 'system-settings'
  | 'countries'
  | 'departments'
  | 'states'
  | 'cities';

// Generic Master Data API
export const masterDataAPI = {
  // Get all items for a master data type
  getAll: async (type: MasterDataType, params?: any) => {
    try {
      const endpoint = `/master-data/${type}`;
      const query = params ? `?${new URLSearchParams(params).toString()}` : '';
      
      const response = await fetch(`${getApiBaseUrl()}${endpoint}${query}`, {
        headers: buildHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      return [];
    }
  },

  // Get single item by ID
  getById: async (type: MasterDataType, id: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/master-data/${type}/${id}`, {
        headers: buildHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type} item`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Error fetching ${type} item:`, error);
      return null;
    }
  },

  // Create new item
  create: async (type: MasterDataType, data: any) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/master-data/${type}`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || `Failed to create ${type} item`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error(`Error creating ${type} item:`, error);
      throw error;
    }
  },

  // Update item
  update: async (type: MasterDataType, id: string, data: any) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/master-data/${type}/${id}`, {
        method: 'PUT',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || `Failed to update ${type} item`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error(`Error updating ${type} item:`, error);
      throw error;
    }
  },

  // Delete item
  delete: async (type: MasterDataType, id: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/master-data/${type}/${id}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || `Failed to delete ${type} item`);
      }

      return true;
    } catch (error) {
      console.error(`Error deleting ${type} item:`, error);
      throw error;
    }
  },

  // Specific helpers for commonly used master data
  cuisineTypes: {
    getAll: () => masterDataAPI.getAll('cuisine-types'),
    create: (data: any) => masterDataAPI.create('cuisine-types', data),
    update: (id: string, data: any) => masterDataAPI.update('cuisine-types', id, data),
    delete: (id: string) => masterDataAPI.delete('cuisine-types', id),
  },

  amenities: {
    getAll: () => masterDataAPI.getAll('amenities'),
    create: (data: any) => masterDataAPI.create('amenities', data),
    update: (id: string, data: any) => masterDataAPI.update('amenities', id, data),
    delete: (id: string) => masterDataAPI.delete('amenities', id),
  },

  foodCategories: {
    getAll: () => masterDataAPI.getAll('food-categories'),
    create: (data: any) => masterDataAPI.create('food-categories', data),
    update: (id: string, data: any) => masterDataAPI.update('food-categories', id, data),
    delete: (id: string) => masterDataAPI.delete('food-categories', id),
  },

  tableStatuses: {
    getAll: () => masterDataAPI.getAll('table-statuses'),
    create: (data: any) => masterDataAPI.create('table-statuses', data),
    update: (id: string, data: any) => masterDataAPI.update('table-statuses', id, data),
    delete: (id: string) => masterDataAPI.delete('table-statuses', id),
  },

  reservationStatuses: {
    getAll: () => masterDataAPI.getAll('reservation-statuses'),
    create: (data: any) => masterDataAPI.create('reservation-statuses', data),
    update: (id: string, data: any) => masterDataAPI.update('reservation-statuses', id, data),
    delete: (id: string) => masterDataAPI.delete('reservation-statuses', id),
  },

  dietaryPreferences: {
    getAll: () => masterDataAPI.getAll('dietary-tags'),
    create: (data: any) => masterDataAPI.create('dietary-tags', data),
    update: (id: string, data: any) => masterDataAPI.update('dietary-tags', id, data),
    delete: (id: string) => masterDataAPI.delete('dietary-tags', id),
  },

  foodTags: {
    getAll: () => masterDataAPI.getAll('food-tags'),
    create: (data: any) => masterDataAPI.create('food-tags', data),
  },

  spiceLevels: {
    getAll: () => masterDataAPI.getAll('spice-levels'),
    create: (data: any) => masterDataAPI.create('spice-levels', data),
    update: (id: string, data: any) => masterDataAPI.update('spice-levels', id, data),
    delete: (id: string) => masterDataAPI.delete('spice-levels', id),
  },

  servingSizes: {
    getAll: () => masterDataAPI.getAll('serving-sizes'),
    create: (data: any) => masterDataAPI.create('serving-sizes', data),
    update: (id: string, data: any) => masterDataAPI.update('serving-sizes', id, data),
    delete: (id: string) => masterDataAPI.delete('serving-sizes', id),
  },

  paymentMethods: {
    getAll: () => masterDataAPI.getAll('payment-methods'),
    create: (data: any) => masterDataAPI.create('payment-methods', data),
    update: (id: string, data: any) => masterDataAPI.update('payment-methods', id, data),
    delete: (id: string) => masterDataAPI.delete('payment-methods', id),
  },

  paymentStatuses: {
    getAll: () => masterDataAPI.getAll('payment-statuses'),
    create: (data: any) => masterDataAPI.create('payment-statuses', data),
    update: (id: string, data: any) => masterDataAPI.update('payment-statuses', id, data),
    delete: (id: string) => masterDataAPI.delete('payment-statuses', id),
  },

  orderStatuses: {
    getAll: () => masterDataAPI.getAll('order-statuses'),
    create: (data: any) => masterDataAPI.create('order-statuses', data),
    update: (id: string, data: any) => masterDataAPI.update('order-statuses', id, data),
    delete: (id: string) => masterDataAPI.delete('order-statuses', id),
  },

  deliveryStatuses: {
    getAll: () => masterDataAPI.getAll('delivery-statuses'),
    create: (data: any) => masterDataAPI.create('delivery-statuses', data),
    update: (id: string, data: any) => masterDataAPI.update('delivery-statuses', id, data),
    delete: (id: string) => masterDataAPI.delete('delivery-statuses', id),
  },

  bookingStatuses: {
    getAll: () => masterDataAPI.getAll('booking-statuses'),
    create: (data: any) => masterDataAPI.create('booking-statuses', data),
  },

  orderTypes: {
    getAll: () => masterDataAPI.getAll('order-types'),
    create: (data: any) => masterDataAPI.create('order-types', data),
  },

  discountTypes: {
    getAll: () => masterDataAPI.getAll('discount-types'),
    create: (data: any) => masterDataAPI.create('discount-types', data),
    update: (id: string, data: any) => masterDataAPI.update('discount-types', id, data),
    delete: (id: string) => masterDataAPI.delete('discount-types', id),
  },

  offerCategories: {
    getAll: () => masterDataAPI.getAll('offer-categories'),
    create: (data: any) => masterDataAPI.create('offer-categories', data),
    update: (id: string, data: any) => masterDataAPI.update('offer-categories', id, data),
    delete: (id: string) => masterDataAPI.delete('offer-categories', id),
  },

  countries: {
    getAll: () => masterDataAPI.getAll('countries'),
    create: (data: any) => masterDataAPI.create('countries', data),
  },

  departments: {
    getAll: () => masterDataAPI.getAll('departments'),
    create: (data: any) => masterDataAPI.create('departments', data),
    update: (id: string, data: any) => masterDataAPI.update('departments', id, data),
    delete: (id: string) => masterDataAPI.delete('departments', id),
  },

  states: {
    getAll: () => masterDataAPI.getAll('states'),
    create: (data: any) => masterDataAPI.create('states', data),
  },

  cities: {
    getAll: (params?: any) => masterDataAPI.getAll('cities', params),
    create: (data: any) => masterDataAPI.create('cities', data),
  },
};

export default masterDataAPI;
